import { useCallback, useMemo, useState } from 'react';

import type {
  IWorkJourneyManagementAttentionItem,
  IWorkJourneyManagementCalendarCell,
} from '@src/models/interfaces/workJourneyManagement.interface';
import { WorkJourneyManagementMapper } from '@src/models/mappers/WorkJourneyManagementMapper';

export type UseWorkJourneyCalendarDayDrawerResult = {
  open: boolean;
  date: string | null;
  items: IWorkJourneyManagementAttentionItem[];
  loading: boolean;
  error: string | null;
  openForCell: (cell: IWorkJourneyManagementCalendarCell) => void;
  close: () => void;
};

/**
 * Day drawer reads the conflicts already embedded in listCalendar cells (no
 * second cloud function call). Rows are derived from the current cells so a
 * refresh after covering a position updates the open drawer.
 */
export const useWorkJourneyCalendarDayDrawer = (
  cells: IWorkJourneyManagementCalendarCell[],
): UseWorkJourneyCalendarDayDrawerResult => {
  const [date, setDate] = useState<string | null>(null);

  const close = useCallback(() => {
    setDate(null);
  }, []);

  const openForCell = useCallback((cell: IWorkJourneyManagementCalendarCell) => {
    if (!cell?.date) return;
    // Future/today cells with no attention problems must not open an empty drawer.
    if (cell.footerAction === 'journeys' && cell.journeyCount === 0) return;
    if (cell.footerAction === 'journeys' && !(cell.conflicts?.length > 0)) return;
    setDate(cell.date);
  }, []);

  const items = useMemo(() => {
    if (!date) return [];
    const cell = cells.find(entry => entry.date === date);
    return WorkJourneyManagementMapper.fromCalendarDayConflicts(cell?.conflicts);
  }, [cells, date]);

  return {
    // Hide the drawer if the last conflict was resolved while it was open.
    open: Boolean(date) && items.length > 0,
    date,
    items,
    loading: false,
    error: null,
    openForCell,
    close,
  };
};
